/**
 * Biometric Authentication Service
 * Uses Web Authentication API for biometric verification
 */

const CREDENTIAL_KEY = 'biometric_credential_id';
const BIOMETRIC_ENABLED_KEY = 'biometric_enabled';

/**
 * Check if biometric authentication is available on this device
 */
export async function isBiometricAvailable() {
  if (!window.PublicKeyCredential) {
    return false;
  }

  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (error) {
    console.error('Error checking biometric availability:', error);
    return false;
  }
}

/**
 * Check if biometric is enabled for this app
 */
export function isBiometricEnabled() {
  return localStorage.getItem(BIOMETRIC_ENABLED_KEY) === 'true';
}

/**
 * Enable biometric authentication
 */
export function enableBiometric() {
  localStorage.setItem(BIOMETRIC_ENABLED_KEY, 'true');
}

/**
 * Disable biometric authentication
 */
export function disableBiometric() {
  localStorage.removeItem(BIOMETRIC_ENABLED_KEY);
  localStorage.removeItem(CREDENTIAL_KEY);
}

/**
 * Generate a random challenge
 */
function generateChallenge() {
  return crypto.getRandomValues(new Uint8Array(32));
}

/**
 * Convert ArrayBuffer to Base64
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert Base64 to ArrayBuffer
 */
function base64ToArrayBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Register biometric credential
 */
export async function registerBiometric() {
  try {
    const challenge = generateChallenge();
    const userId = crypto.getRandomValues(new Uint8Array(16));

    const credential = await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: {
          name: 'Galaxy Mini',
          id: window.location.hostname,
        },
        user: {
          id: userId,
          name: 'user',
          displayName: 'Galaxy Mini User',
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' }, // ES256
          { alg: -257, type: 'public-key' }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
          requireResidentKey: false,
        },
        timeout: 60000,
        attestation: 'none',
      },
    });

    if (credential) {
      // Store credential ID
      const credentialId = arrayBufferToBase64(credential.rawId);
      localStorage.setItem(CREDENTIAL_KEY, credentialId);
      enableBiometric();
      return true;
    }

    return false;
  } catch (error) {
    console.error('Biometric registration failed:', error);
    throw error;
  }
}

/**
 * Verify biometric
 */
export async function verifyBiometric() {
  try {
    const credentialIdBase64 = localStorage.getItem(CREDENTIAL_KEY);
    
    if (!credentialIdBase64) {
      throw new Error('No biometric credential found');
    }

    const credentialId = base64ToArrayBuffer(credentialIdBase64);
    const challenge = generateChallenge();

    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        allowCredentials: [
          {
            id: credentialId,
            type: 'public-key',
            transports: ['internal'],
          },
        ],
        userVerification: 'required',
        timeout: 60000,
      },
    });

    return assertion !== null;
  } catch (error) {
    console.error('Biometric verification failed:', error);
    throw error;
  }
}
